import { HeaderActionButtonProps, LargeMenuProps } from '@vfpt/common-fe-components';
import type { THeader, THeaderLinkItem, THeaderLinkModel, THeaderLinkAssemblyModel, TMenuItem } from '../../types';
import { LoginProps } from './Header.types';

// Guards
function isMenuItem(item: TMenuItem | null): item is TMenuItem {
    return item !== null;
}

function isValidLink(link: THeaderLinkItem): link is THeaderLinkModel & { label: string; url: string } {
    return (
        typeof link === 'object' &&
        link !== null &&
        'label' in link &&
        'url' in link &&
        typeof (link as THeaderLinkModel).label === 'string' &&
        typeof (link as THeaderLinkModel).url === 'string'
    );
}

function isLinkAssembly(item: THeaderLinkItem): item is THeaderLinkAssemblyModel {
    return (
        typeof item === 'object' &&
        item !== null &&
        'linksCollection' in item &&
        Array.isArray((item as THeaderLinkAssemblyModel).linksCollection.items)
    );
}

function isLinkModel(item: THeaderLinkItem): item is THeaderLinkModel & { url: string } {
    return (
        typeof item === 'object' && item !== null && 'url' in item && typeof (item as THeaderLinkModel).url === 'string'
    );
}

// Mappers
export function mapHeaderToMenuItems(headerData: THeader): TMenuItem[] {
    return headerData.secondaryHeaderLeftCollection?.items?.map(mapToMenuItem).filter(isMenuItem) ?? [];
}

export function mapHeaderToMenuItemsRightSection(headerData: THeader): TMenuItem[] {
    return headerData.secondaryHeaderRightCollection?.items?.map(mapToMenuItem).filter(isMenuItem) ?? [];
}

export function mapHeaderToLargeMenuLeft(headerData: THeader): LargeMenuProps['largeMenuItemsLeft'] {
    return (
        headerData.topHeaderLeftCollection?.items?.[0]?.linksCollection?.items?.filter(isValidLink).map((l) => ({
            children: l.label,
            href: l.url,
            label: l.label
        })) ?? []
    );
}

export function mapHeaderToLargeMenuRight(headerData: THeader) {
    const group = headerData.topHeaderRightCollection?.items?.find(isLinkAssembly);
    if (!group) return [];

    return group.linksCollection.items.filter(isValidLink).map((l) => ({
        children: l.label,
        href: l.url,
        label: l.label,
        name: l.name
    }));
}

export function mapHeaderToControlButtons(
    headerData: THeader,
    login: LoginProps
): {
    actionButtons: HeaderActionButtonProps[];
} {
    return {
        actionButtons:
            headerData.secondaryHeaderRightCollection?.items?.[0]?.linksCollection?.items
                ?.filter(isLinkModel)
                .map((l) => {
                    const { isLoggedIn, onLogin, onLogout } = login;
                    const isLoginLink = l.name === 'Login';
                    const label = isLoginLink ? (isLoggedIn ? 'Logout' : 'Login') : l.label;

                    return {
                        ariaLabel: label,
                        name: label,
                        label: label,
                        onClick: isLoginLink ? (isLoggedIn ? onLogout : onLogin) : () => {},
                        avatar: isLoginLink && isLoggedIn ? login.loggedInInitials : undefined
                    };
                }) ?? []
    };
}

// Internals
function mapToMenuItem(item: THeaderLinkItem): TMenuItem | null {
    if (isLinkAssembly(item)) {
        const children = item.linksCollection.items.map(mapToMenuItem).filter(isMenuItem);

        if (!children.length) return null;

        return {
            title: item.label || item.name,
            expandLabel: 'Expandir',
            collapseLabel: 'Fechar',
            subMenuItems: children,
            name: item.name
        };
    }

    if (isLinkModel(item)) {
        return {
            title: item.label || item.name,
            href: item.url,
            name: item.name
        };
    }

    return null;
}
